import React from 'react';
import { ButtonGroup, Dropdown } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';

export type LomavisCampaignActionDropdownItem1 = {
  disabled?: boolean;
  key: string;
  label: string;
  onClick: () => void;
};

export type LomavisCampaignActionDropdownPrimary1 = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

export const LomavisCampaignActionDropdown1: React.FC<{
  items: LomavisCampaignActionDropdownItem1[];
  isBusy?: boolean;
  ariaLabel?: string;
  drop?: 'up' | 'down' | 'start' | 'end';
  primaryAction?: LomavisCampaignActionDropdownPrimary1;
  primaryVariant?: string;
  // Default 'fixed' escapes overflow-hidden ancestors (needed inside the
  // campaigns table). Pass 'absolute' when the dropdown lives in a card
  // without clipping — 'fixed' + drop="up" races on the first open and
  // lands the menu at the viewport's bottom-right corner.
  popperStrategy?: 'fixed' | 'absolute';
}> = ({
  items,
  isBusy = false,
  ariaLabel,
  drop = 'up',
  primaryAction,
  primaryVariant = 'secondary',
  popperStrategy = 'fixed'
}) => {
  const { t } = useTranslate();

  if (items.length === 0 && !primaryAction) return null;

  const toggleAriaLabel =
    ariaLabel || t('adManager.toggleCampaignActions', 'Toggle campaign actions');

  // Two per-caller knobs, both fighting Popper edge cases seen in this PR:
  // - renderOnMount only when strategy: 'fixed' — that combo races on first
  //   open (menu lands at (0,0)) unless popper measures the toggle before
  //   the click. absolute-positioned poppers don't have this race and
  //   renderOnMount there caused a "flash then vanish" instead.
  // - Flip only disabled for drop="up" — the Publish button sits near the
  //   viewport bottom, so flip would slide the menu downward off-screen.
  //   Row actions use drop="down" and benefit from flip when the row is
  //   near the bottom edge of the table.
  const shouldRenderOnMount = popperStrategy === 'fixed';
  const popperModifiers =
    drop === 'up' ? [{ name: 'flip', enabled: false }] : [];
  const menu = (
    <Dropdown.Menu
      renderOnMount={shouldRenderOnMount}
      popperConfig={{
        strategy: popperStrategy,
        modifiers: popperModifiers
      }}
    >
      {items.map((action) => (
        <Dropdown.Item
          key={action.key}
          as="button"
          type="button"
          disabled={isBusy || action.disabled}
          onClick={() => action.onClick()}
        >
          <div className="d-flex align-items-center justify-content-between fs-5">
            <span>{action.label}</span>
          </div>
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  );

  const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

  if (primaryAction) {
    return (
      <div onClick={stopRowClick} className="d-inline-block">
        <Dropdown as={ButtonGroup} drop={drop} align="end" size="sm">
          <button
            type="button"
            className={`btn btn-sm btn-${primaryVariant}`}
            disabled={isBusy || primaryAction.disabled}
            onClick={(event) => {
              event.stopPropagation();
              primaryAction.onClick();
            }}
          >
            {primaryAction.label}
          </button>
          <Dropdown.Toggle
            split
            variant={primaryVariant}
            size="sm"
            aria-label={toggleAriaLabel}
            disabled={isBusy || items.length === 0}
          />
          {menu}
        </Dropdown>
      </div>
    );
  }

  return (
    <div onClick={stopRowClick} className="d-inline-block">
      <Dropdown drop={drop} align="end">
        <Dropdown.Toggle
          as="button"
          type="button"
          aria-label={toggleAriaLabel}
          disabled={isBusy || items.length === 0}
          className="btn btn-sm btn-secondary d-inline-flex align-items-center justify-content-center px-2"
          style={{ minWidth: '2rem' }}
        >
          <span aria-hidden="true" style={{ lineHeight: 1, fontSize: '1.25rem' }}>
            &#8942;
          </span>
        </Dropdown.Toggle>
        {menu}
      </Dropdown>
    </div>
  );
};
